import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  NgZone,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { take } from 'rxjs/operators';
import {
  CustomPlaceInput,
  LocationsService,
  PlaceDetail,
} from '../../../../services/locations.service';
import { REASON_FIRST, apiErrorText } from '../workspace/api-errors';
import { kindLabel } from './place-kinds';

/**
 * Add a place that isn't in the crawl, or edit one added by hand (#253 step 3; Ivan 2026-10-17). A typed name, a type,
 * and a pin: the host puts the map into pin mode while this is open, so a click on the map places it (the suburb's
 * centre to start). Address, website and phone are optional. Nothing comes from Google: a person types it all.
 */
@Component({
  selector: 'app-suburb-custom-place',
  templateUrl: './suburb-custom-place.component.html',
  styleUrls: ['./suburb-custom-place.component.scss'],
})
export class SuburbCustomPlaceComponent implements OnInit, OnChanges {
  /** The suburb it's added in. */
  @Input() salCode!: string;
  @Input() suburbName = '';
  /** Editing this place (added by hand); null adds a new one. */
  @Input() place: PlaceDetail | null = null;
  /** Where the pin is (the host owns it: map clicks move it). */
  @Input() pin: { lat: number; lng: number } | null = null;
  @Output() saved = new EventEmitter<PlaceDetail>();
  @Output() deleted = new EventEmitter<number>();
  @Output() cancelled = new EventEmitter<void>();

  kinds: Array<{ label: string; value: string }> = [];
  form: {
    name: string;
    kind: string | null;
    street: string;
    postcode: string;
    website: string;
    phone: string;
  } = {
    name: '',
    kind: null,
    street: '',
    postcode: '',
    website: '',
    phone: '',
  };
  busy = false;
  error = '';

  constructor(
    private api: LocationsService,
    private zone: NgZone,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.api
      .customKinds()
      .pipe(take(1))
      .subscribe({
        next: (kinds) =>
          this.zone.run(() => {
            this.kinds = kinds
              .map((k) => ({ label: kindLabel(k), value: k }))
              .sort((a, b) => a.label.localeCompare(b.label));
            this.cdr.detectChanges();
          }),
        error: () => {},
      });
  }

  ngOnChanges(c: SimpleChanges): void {
    if (c.place) {
      const p = this.place;
      this.form = {
        name: p?.crawlName ?? '',
        kind: p?.kind ?? null,
        street: p?.custom?.street ?? '',
        postcode: p?.custom?.postcode ?? '',
        website: p?.custom?.website ?? '',
        phone: p?.custom?.phone ?? '',
      };
      this.error = '';
    }
  }

  get editing(): boolean {
    return !!this.place;
  }

  /** Delete is only for a place that isn't on the website or in Conductor. */
  get deleteBlockedBy(): string | null {
    const p = this.place;
    if (!p) return null;
    if (p.provider) return 'It is in Conductor: remove it from Conductor first.';
    if (p.listing !== 'none') return 'It is on the website: remove it from the website first.';
    return null;
  }

  get canSave(): boolean {
    return !this.busy && !!this.form.name.trim() && !!this.form.kind && !!this.pin;
  }

  private fail(e: unknown): string {
    return apiErrorText(e, 'something went wrong', REASON_FIRST);
  }

  save(): void {
    if (!this.canSave || !this.pin) return;
    const f = this.form;
    const body: CustomPlaceInput = {
      name: f.name.trim(),
      kind: f.kind!,
      lat: this.pin.lat,
      lng: this.pin.lng,
      street: f.street.trim() || null,
      postcode: f.postcode.trim() || null,
      website: f.website.trim() || null,
      phone: f.phone.trim() || null,
    };
    this.busy = true;
    this.error = '';
    const req = this.place
      ? this.api.updateCustomPlace(this.place.locationId, body)
      : this.api.createCustomPlace({ ...body, salCode: this.salCode });
    req.pipe(take(1)).subscribe({
      next: (d) =>
        this.zone.run(() => {
          this.busy = false;
          this.saved.emit(d);
        }),
      error: (e) =>
        this.zone.run(() => {
          this.busy = false;
          this.error = this.fail(e);
          this.cdr.detectChanges();
        }),
    });
  }

  remove(): void {
    const p = this.place;
    if (
      !p ||
      this.deleteBlockedBy ||
      !window.confirm(
        `Delete "${p.crawlName}"? It was added by hand; this takes it out of the Suburbs tool.`
      )
    )
      return;
    this.busy = true;
    this.api
      .deleteCustomPlace(p.locationId)
      .pipe(take(1))
      .subscribe({
        next: () =>
          this.zone.run(() => {
            this.busy = false;
            this.deleted.emit(p.locationId);
          }),
        error: (e) =>
          this.zone.run(() => {
            this.busy = false;
            this.error = this.fail(e);
            this.cdr.detectChanges();
          }),
      });
  }
}
